import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Add,
  Sort,
  ChevronRight,
  FolderOutlined,
  VisibilityOff,
} from '@mui/icons-material';
import { MoreActionsMenuButton } from '../../../toolympus/components/primitives';
import { CopTreeNode, CopTreeNodeSchema } from './useCopTreeNodes';
import { TableDisplay } from '../../../toolympus/components/schemed/TableDisplay';

export interface ColumnData {
  parent?: CopTreeNode;
  items: CopTreeNode[];
}

export interface CopTreeFinderProps {
  columns: ColumnData[];
  selectedPathIds: number[];
  matchedNodeIds?: Set<number>;
  filterText?: string;
  onSelect: (columnIndex: number, node: CopTreeNode) => void;
  onEdit: (node: CopTreeNode) => void;
  onDelete: (node: CopTreeNode) => void;
  onAdd: (parentNode?: CopTreeNode) => void;
  onMove: (node: CopTreeNode) => void;
  onReorder: (items: CopTreeNode[]) => void;
  hasChildren: (node: CopTreeNode) => boolean;
}

export const CopTreeFinder: React.FC<CopTreeFinderProps> = ({
  columns,
  selectedPathIds,
  matchedNodeIds,
  filterText,
  onSelect,
  onEdit,
  onDelete,
  onAdd,
  onMove,
  onReorder,
  hasChildren,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        height: 'calc(100vh - 175px)',
        minHeight: 450,
        overflowX: 'auto',
        overflowY: 'hidden',
        borderTop: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      {columns.map((column, columnIndex) => {
        const selectedIdInThisCol = selectedPathIds[columnIndex];

        return (
          <Box
            key={column.parent?._id ?? 'root'}
            sx={{
              width: 300,
              minWidth: 300,
              maxWidth: 320,
              display: 'flex',
              flexDirection: 'column',
              borderRight: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            {/* Flat Column Header */}
            <Box
              sx={{
                px: 1.5,
                py: 1,
                minHeight: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.50',
              }}
            >
              <Box sx={{ overflow: 'hidden', mr: 1 }}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: 600, color: 'text.primary' }}
                  title={column.parent?.title ?? 'Root Nodes'}
                >
                  {column.parent?.title ?? (
                    <FormattedMessage id="cop_tree.root_nodes" defaultMessage="Root Nodes" />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {column.items.length}{' '}
                  <FormattedMessage id="common.items" defaultMessage="items" />
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Tooltip title={<FormattedMessage id="common.add" defaultMessage="Add node here" />}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => onAdd(column.parent)}
                  >
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title={<FormattedMessage id="common.reorder" defaultMessage="Reorder" />}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      disabled={column.items.length < 2}
                      onClick={() => onReorder(column.items)}
                    >
                      <Sort fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>

            {/* Flat Node Items List */}
            <List
              dense
              disablePadding
              sx={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {column.items.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    <FormattedMessage id="common.no_items" defaultMessage="No items" />
                  </Typography>
                </Box>
              ) : (
                column.items.map(item => {
                  const isSelected = selectedIdInThisCol === item._id;
                  const itemHasChildren = hasChildren(item);
                  const isDirectMatch = Boolean(filterText && matchedNodeIds?.has(item._id));

                  return (
                    <ListItemButton
                      key={item._id}
                      selected={isSelected}
                      onClick={() => onSelect(columnIndex, item)}
                      onDoubleClick={() => onEdit(item)}
                      sx={{
                        py: 0.75,
                        px: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'action.hover',
                        backgroundColor: isDirectMatch && !isSelected ? 'warning.lighter' : undefined,
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: 'primary.contrastText',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                          '& .MuiTypography-root': {
                            color: 'primary.contrastText',
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'primary.contrastText',
                          },
                          '& .MuiChip-root': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'primary.contrastText',
                          },
                        },
                      }}
                    >
                      {/* Color Dot or Folder Icon */}
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        {item.color ? (
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: item.color,
                              border: '1px solid rgba(0,0,0,0.15)',
                            }}
                          />
                        ) : (
                          <FolderOutlined sx={{ fontSize: 18 }} />
                        )}
                      </ListItemIcon>

                      {/* Title & Type Tag */}
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                fontWeight: isDirectMatch || isSelected ? 600 : 400,
                              }}
                            >
                              {item.title}
                            </Typography>
                            {item.is_hidden && (
                              <VisibilityOff sx={{ fontSize: 13, opacity: 0.7 }} />
                            )}
                          </Box>
                        }
                        secondary={
                          item.type ? (
                            <Chip
                              label={<TableDisplay field="type" row={item} schema={CopTreeNodeSchema.type} />}
                              size="small"
                              sx={{
                                height: 16,
                                fontSize: '0.65rem',
                                mt: 0.25,
                              }}
                            />
                          ) : null
                        }
                        secondaryTypographyProps={{ component: 'div' }}
                      />

                      {/* Row Menu */}
                      <Box
                        onClick={e => e.stopPropagation()}
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <MoreActionsMenuButton
                          actions={[
                            [
                              'edit',
                              <FormattedMessage id="common.edit" defaultMessage="Edit" />,
                              () => onEdit(item),
                            ],
                            [
                              'add_child',
                              <FormattedMessage id="cop_tree.add_child" defaultMessage="Add child node" />,
                              () => onAdd(item),
                            ],
                            [
                              'move',
                              <FormattedMessage id="common.move" defaultMessage="Move..." />,
                              () => onMove(item), // <--- Added
                            ],
                            [
                              'remove',
                              <FormattedMessage id="common.delete" defaultMessage="Delete" />,
                              () => onDelete(item),
                            ],
                          ]}
                        />
                      </Box>

                      {/* Child Indicator */}
                      {itemHasChildren && (
                        <ChevronRight
                          fontSize="small"
                          sx={{ opacity: isSelected ? 1 : 0.4, ml: 0.5 }}
                        />
                      )}
                    </ListItemButton>
                  );
                })
              )}
            </List>
          </Box>
        );
      })}
    </Box>
  );
};